import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SeloDestaque } from "@/components/SeloDestaque";
import { ExcluirCadastroBtn } from "@/components/ExcluirCadastroBtn";

const STATUS_COLORS = {
  ativo: "bg-[#00A859] text-white",
  inativo: "bg-[#71717A] text-white",
  ferias: "bg-[#FFCC00] text-foreground",
  bloqueado: "bg-[#E60000] text-white",
  cancelado: "bg-foreground text-white",
};

const CICLO_LABELS = {
  cadastro_incompleto: "Cadastro Incompleto", aguardando_documentos: "Aguardando Documentos",
  em_analise: "Em Análise", pendencia_documental: "Pendência Documental",
  ativo: "Ativo", restrito: "Restrito", em_observacao: "Em Observação",
  suspenso_preventivamente: "Suspenso Preventivamente", inativo: "Inativo",
  bloqueado: "Bloqueado", encerrado: "Encerrado",
};

const CICLO_COLORS = {
  ativo: "bg-[#DCFCE7] text-[#166534]",
  em_analise: "bg-[#DBEAFE] text-[#142766]",
  aguardando_documentos: "bg-aj-amber-soft text-aj-orange-hover",
  pendencia_documental: "bg-aj-amber-soft text-aj-orange-hover",
  restrito: "bg-orange-100 text-orange-800",
  em_observacao: "bg-orange-100 text-orange-800",
  suspenso_preventivamente: "bg-red-100 text-[#B91C1C]",
  inativo: "bg-[#F4F4F5] text-[#71717A]",
  bloqueado: "bg-red-100 text-[#B91C1C]",
  encerrado: "bg-foreground/10 text-foreground",
  cadastro_incompleto: "bg-[#F4F4F5] text-[#71717A]",
};

export default function Montadores() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [novoStatus, setNovoStatus] = useState("ativo");
  const [motivo, setMotivo] = useState("");
  const [novoCiclo, setNovoCiclo] = useState("");
  const [motivoCiclo, setMotivoCiclo] = useState("");
  const [busca, setBusca] = useState("");
  const [fCiclo, setFCiclo] = useState("");
  const [fVerif, setFVerif] = useState("");
  const [fCidade, setFCidade] = useState("");
  const [fUF, setFUF] = useState("");
  const [fExp, setFExp] = useState("");
  const [fTipo, setFTipo] = useState("");
  const [fScoreMin, setFScoreMin] = useState("");
  const [fScoreMax, setFScoreMax] = useState("");
  const isAdmin = localStorage.getItem("pim_role") === "admin";

  const load = () => {
    if (!isAdmin) { api.get("/montadores").then(r => setList(r.data)); return; }
    const p = new URLSearchParams();
    if (busca) p.set("q", busca);
    if (fCidade) p.set("cidade", fCidade);
    if (fUF) p.set("estado", fUF);
    if (fCiclo) p.set("status_ciclo", fCiclo);
    if (fExp) p.set("experiencia", fExp);
    if (fVerif) p.set("verificado", fVerif);
    if (fTipo) p.set("tipo_pessoa", fTipo);
    if (fScoreMin) p.set("score_min", fScoreMin);
    if (fScoreMax) p.set("score_max", fScoreMax);
    api.get(`/admin/montadores-gestao?${p.toString()}`).then(r => setList(r.data))
      .catch(e => toast.error(formatApiError(e)));
  };
  useEffect(() => { load(); }, [busca, fCidade, fUF, fCiclo, fExp, fVerif, fTipo, fScoreMin, fScoreMax]); // eslint-disable-line

  const link = `${window.location.origin}/cadastro-montador`;

  const aplicarStatus = async () => {
    try {
      const fd = new FormData();
      fd.append("status", novoStatus);
      if (motivo) fd.append("motivo", motivo);
      await api.put(`/montadores/${editing.id}/status`, fd);
      toast.success("Status atualizado");
      setEditing(null); setMotivo(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const desbloquear = async (m) => {
    try {
      await api.post(`/montadores/${m.id}/desbloquear`);
      toast.success("Montador desbloqueado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const aplicarCiclo = async () => {
    if (!novoCiclo) { toast.error("Selecione o ciclo"); return; }
    try {
      await api.put(`/admin/montadores/${editing.id}/status-ciclo`,
        { status_ciclo: novoCiclo, motivo: motivoCiclo || null });
      toast.success("Ciclo do montador atualizado");
      setEditing(null); setNovoCiclo(""); setMotivoCiclo(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-cap">Cadastros</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Montadores</h1>
        </div>
        <div className="text-right">
          <div className="label-cap mb-2">Link público de cadastro</div>
          <div className="flex gap-2">
            <input value={link} readOnly
              className="border border-border bg-[#F4F4F5] px-3 py-2 text-xs font-mono w-80"
              data-testid="link-cadastro" />
            <Button variant="outline" className="btn-sharp"
              onClick={() => { navigator.clipboard.writeText(link); toast.success("Link copiado"); }}
              data-testid="btn-copiar-link">Copiar</Button>
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <Input placeholder="Buscar nome, CPF, email…" value={busca} onChange={(e) => setBusca(e.target.value)}
          className="w-56" data-testid="montadores-busca" />
        <Input placeholder="Cidade" value={fCidade} onChange={(e) => setFCidade(e.target.value)}
          className="w-32" data-testid="montadores-filtro-cidade" />
        <Input placeholder="UF" value={fUF} maxLength={2} onChange={(e) => setFUF(e.target.value.toUpperCase())}
          className="w-16" data-testid="montadores-filtro-uf" />
        <select value={fCiclo} onChange={(e) => setFCiclo(e.target.value)}
          className="border border-border rounded-md text-sm px-2 py-2 bg-white" data-testid="montadores-filtro-ciclo">
          <option value="">Ciclo: todos</option>
          {Object.entries(CICLO_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={fExp} onChange={(e) => setFExp(e.target.value)}
          className="border border-border rounded-md text-sm px-2 py-2 bg-white" data-testid="montadores-filtro-experiencia">
          <option value="">Experiência: todas</option>
          {["NOVO", "EM CRESCIMENTO", "ESTABELECIDO", "EXPERIENTE", "ALTO HISTÓRICO"].map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={fTipo} onChange={(e) => setFTipo(e.target.value)}
          className="border border-border rounded-md text-sm px-2 py-2 bg-white" data-testid="montadores-filtro-tipo">
          <option value="">PF/MEI/PJ</option>
          <option value="pf">Pessoa Física</option>
          <option value="mei">MEI/PJ (com CNPJ)</option>
        </select>
        <select value={fVerif} onChange={(e) => setFVerif(e.target.value)}
          className="border border-border rounded-md text-sm px-2 py-2 bg-white" data-testid="montadores-filtro-verificado">
          <option value="">Docs: todos</option>
          <option value="true">Verificados</option>
          <option value="false">Não verificados</option>
        </select>
        <div className="flex items-center gap-1">
          <Input placeholder="Score ≥" type="number" value={fScoreMin} onChange={(e) => setFScoreMin(e.target.value)}
            className="w-20" data-testid="montadores-filtro-score-min" />
          <Input placeholder="≤" type="number" value={fScoreMax} onChange={(e) => setFScoreMax(e.target.value)}
            className="w-20" data-testid="montadores-filtro-score-max" />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="montadores-total">{list.length} resultado(s)</span>
      </div>

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-[#F4F4F5]">
            <tr><Th>Nome</Th><Th>CPF</Th><Th>Cidade</Th><Th>Cap.</Th><Th>Score</Th><Th>Experiência</Th><Th>Contrato</Th><Th>Status</Th><Th>Ciclo</Th><Th></Th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">Nenhum montador cadastrado. Compartilhe o link acima.</td></tr>}
            {list.filter(m =>
              (!busca || `${m.nome} ${m.cpf} ${m.email} ${m.cidade}`.toLowerCase().includes(busca.toLowerCase())) &&
              (!fCiclo || m.status_ciclo === fCiclo) &&
              (!fVerif || String(!!m.documentos_verificados) === fVerif)
            ).map((m) => (
              <tr key={m.id} className="border-b border-border last:border-0" data-testid={`montador-row-${m.id}`}>
                <Td><strong>{m.nome}</strong>{m.destaque && (
                  <span className="ml-1.5 inline-block align-middle"><SeloDestaque show /></span>
                )}{m.documentos_verificados && (
                  <span className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-bold text-[#166534]" title="Documentos verificados"
                    data-testid={`selo-verificado-${m.id}`}>✓ Verificado</span>
                )}<div className="text-xs text-muted-foreground">{m.email}</div></Td>
                <Td className="font-mono text-xs">{m.cpf}</Td>
                <Td>{m.cidade}/{m.estado}</Td>
                <Td className="font-mono">{m.capacidade_semanal}/sem</Td>
                <Td className="font-mono">{m.score?.toFixed?.(0) ?? "—"}</Td>
                <Td>
                  <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase bg-[#DBEAFE] text-[#142766] rounded"
                    data-testid={`montador-experiencia-${m.id}`}>
                    {m.nivel_experiencia || "NOVO"}
                  </span>
                </Td>
                <Td>{m.contrato_assinado ? "✓" : "—"}</Td>
                <Td>
                  <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${STATUS_COLORS[m.status] || "bg-secondary"}`}>
                    {m.status}
                  </span>
                  {m.motivo_status && <div className="text-xs text-muted-foreground mt-1">{m.motivo_status}</div>}
                </Td>
                <Td>
                  <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${CICLO_COLORS[m.status_ciclo] || "bg-secondary"}`}
                    data-testid={`montador-ciclo-${m.id}`}>
                    {CICLO_LABELS[m.status_ciclo] || "—"}
                  </span>
                </Td>
                <Td>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="btn-sharp h-8 text-xs"
                      onClick={() => { setEditing(m); setNovoStatus(m.status); setMotivo(m.motivo_status || ""); setNovoCiclo(m.status_ciclo || ""); setMotivoCiclo(""); }}
                      data-testid={`montador-editar-${m.id}`}>
                      Status
                    </Button>
                    <Button size="sm" variant="outline" className="btn-sharp"
                      onClick={() => window.location.assign(`/admin/montadores/${m.id}`)}
                      data-testid={`montador-ficha-${m.id}`}>
                      Ficha
                    </Button>
                    {m.status === "bloqueado" && (
                      <Button size="sm" className="btn-sharp h-8 text-xs brand-bg text-white"
                        onClick={() => desbloquear(m)} data-testid={`montador-desbloquear-${m.id}`}>
                        Desbloquear
                      </Button>
                    )}
                    <span className="flex items-center px-1">
                      <ExcluirCadastroBtn endpoint={`/admin/montadores/${m.id}`} nome={m.nome}
                        onExcluido={load} testid={`montador-excluir-${m.id}`} />
                    </span>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alterar status — {editing?.nome}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="label-cap">Novo status</Label>
              <Select value={novoStatus} onValueChange={setNovoStatus}>
                <SelectTrigger className="mt-2" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ativo">Ativo</SelectItem>
                  <SelectItem value="inativo">Inativo</SelectItem>
                  <SelectItem value="ferias">Férias</SelectItem>
                  <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {novoStatus !== "ativo" && (
              <div>
                <Label className="label-cap">Motivo *</Label>
                <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} className="mt-2" data-testid="status-motivo" />
              </div>
            )}
            <Button onClick={aplicarStatus} className="w-full btn-sharp brand-bg text-white" data-testid="status-aplicar">
              Aplicar
            </Button>
            <div className="border-t border-border pt-4">
              <Label className="label-cap">Ciclo do montador (regra definitiva)</Label>
              <Select value={novoCiclo} onValueChange={setNovoCiclo}>
                <SelectTrigger className="mt-2" data-testid="ciclo-select"><SelectValue placeholder="Selecionar ciclo" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CICLO_LABELS).map(([v, l]) => (
                    <SelectItem key={v} value={v}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {novoCiclo && !["ativo", "em_analise", "aguardando_documentos", "pendencia_documental", "cadastro_incompleto"].includes(novoCiclo) && (
                <div className="mt-3">
                  <Label className="label-cap">Motivo do ciclo *</Label>
                  <Input value={motivoCiclo} onChange={(e) => setMotivoCiclo(e.target.value)} className="mt-2" data-testid="ciclo-motivo" />
                </div>
              )}
              <Button onClick={aplicarCiclo} variant="outline" className="w-full btn-sharp mt-3" data-testid="ciclo-aplicar">
                Aplicar ciclo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Th({ children }) { return <th className="text-left p-3 label-cap font-medium">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
